import {
  ActionIcon,
  Button,
  FileInput,
  TextInput,
  type ActionIconProps,
  type ButtonProps,
  type TextInputProps,
} from '@mantine/core';
import { isVoiceMessageFile, VOICE_MESSAGE_FILE_ACCEPT } from '../../voice';
import { IconMicrophone, IconPaperclip, IconSend } from '@tabler/icons-react';
import { useRef, type ReactNode } from 'react';

export const ChatComposerInput = (props: TextInputProps) => (
  <TextInput
    variant='unstyled'
    bg='primary.0'
    w='100%'
    styles={{ root: { borderRadius: '12px' } }}
    {...props}
  />
);

type ChatActionIconProps = {
  title: string;
  disabled?: boolean;
  variant?: ActionIconProps['variant'];
};

export type ChatAttachButtonProps = ChatActionIconProps & {
  value: File | null;
  onSelect: (file: File) => void;
  /** The file is not OGG or WebM audio the message endpoints accept. */
  onInvalidFile: (file: File) => void;
};

export const ChatAttachButton = ({
  title,
  disabled,
  variant,
  value,
  onSelect,
  onInvalidFile,
}: ChatAttachButtonProps) => {
  const fileInputRef = useRef<HTMLButtonElement>(null);

  const handleChange = (file: File | null) => {
    if (!file) {
      return;
    }
    if (!isVoiceMessageFile(file)) {
      onInvalidFile(file);
      return;
    }
    onSelect(file);
  };

  return (
    <>
      <ActionIcon
        size='md'
        variant={variant}
        onClick={() => fileInputRef.current?.click()}
        title={title}
        disabled={disabled}
      >
        <IconPaperclip />
      </ActionIcon>
      <FileInput
        ref={fileInputRef}
        accept={VOICE_MESSAGE_FILE_ACCEPT}
        value={value}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
    </>
  );
};

export const ChatVoiceButton = ({
  title,
  disabled,
  variant,
  onClick,
}: ChatActionIconProps & { onClick: () => void }) => (
  <ActionIcon
    size='md'
    variant={variant}
    onClick={onClick}
    title={title}
    disabled={disabled}
  >
    <IconMicrophone />
  </ActionIcon>
);

export type ChatSendButtonProps = ButtonProps & {
  onClick: () => void;
  children: ReactNode;
};

export const ChatSendButton = (props: ChatSendButtonProps) => (
  <Button size='md' leftSection={<IconSend size={20} />} w={160} {...props} />
);
